import React from 'react';
import { Send } from 'lucide-react';

interface BotChatInputProps {
  /** Current input value */
  value: string;
  /** Whether a message is currently being sent */
  disabled: boolean;
  /** Callback when input value changes */
  onChange: (value: string) => void;
  /** Callback when send is triggered */
  onSend: () => void;
  /** Ref to the textarea element */
  inputRef: React.RefObject<HTMLTextAreaElement>;
}

/**
 * Input component for BotChat.
 * Handles message input and send button.
 */
export function BotChatInput({
  value,
  disabled,
  onChange,
  onSend,
  inputRef,
}: BotChatInputProps) {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Always block Enter (without Shift) to prevent newline insertion
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // Only send if not disabled and has content
      if (!disabled && value.trim()) {
        onSend();
      }
    }
  };

  return (
    <div className="flex-shrink-0 border-t border-surface-600 p-3">
      <div className="flex items-end gap-2">
        <textarea
          ref={inputRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask anything..."
          rows={1}
          className="flex-1 resize-none bg-surface-800 border border-surface-600 rounded-lg px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-primary-500 min-h-[40px] max-h-[80px]"
        />
        <button
          onClick={onSend}
          disabled={!value.trim() || disabled}
          aria-label="Send message"
          className="p-2 bg-primary-500 hover:bg-primary-400 disabled:bg-surface-700 disabled:text-text-muted text-surface-900 rounded-lg transition-colors"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
